import React, { useState, useEffect } from 'react';
import { Check, X, Trash2, MessageCircle } from 'lucide-react';
import { adminCommentService } from '../../services/adminCommentService';
import { toast } from 'sonner';

const PostCommentSection = ({ postId }) => {
    const [comments, setComments] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });

    useEffect(() => {
        if (postId) {
            fetchComments();
        }
    }, [postId, filter]);

    const fetchComments = async () => {
        try {
            setLoading(true);
            const data = await adminCommentService.getComments({
                post_id: postId,
                status: filter === 'all' ? undefined : filter,
                per_page: 50
            });
            setComments(data.comments || []);
            setStats(data.stats || { total: 0, approved: 0, pending: 0 });
        } catch (error) {
            console.error('Error fetching comments:', error);
            toast.error('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            await adminCommentService.approveComment(id);
            toast.success('Comment approved');
            fetchComments();
        } catch (error) {
            console.error('Error approving comment:', error);
            toast.error('Failed to approve comment');
        }
    };

    const handleReject = async (id) => {
        try {
            await adminCommentService.rejectComment(id);
            toast.success('Comment rejected');
            fetchComments();
        } catch (error) {
            console.error('Error rejecting comment:', error);
            toast.error('Failed to reject comment');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this comment permanently?')) {
            return;
        }

        try {
            await adminCommentService.deleteComment(id);
            toast.success('Comment deleted');
            fetchComments();
        } catch (error) {
            console.error('Error deleting comment:', error);
            toast.error('Failed to delete comment');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5" style={{ color: '#31415A' }} />
                    <h3 className="text-lg font-bold" style={{ color: '#31415A' }}>
                        Comments ({stats.total})
                    </h3>
                </div>

                {/* Stats Badges */}
                <div className="flex items-center gap-2">
                    {stats.pending > 0 && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded-full">
                            🟡 {stats.pending} Pending
                        </span>
                    )}
                    {stats.approved > 0 && (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
                            ✅ {stats.approved} Approved
                        </span>
                    )}
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 pb-3">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'all'
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    All ({stats.total})
                </button>
                <button
                    onClick={() => setFilter('pending')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'pending'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    Pending ({stats.pending})
                </button>
                <button
                    onClick={() => setFilter('approved')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'approved'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    Approved ({stats.approved})
                </button>
            </div>

            {/* Comments List */}
            {loading ? (
                <div className="text-center py-8">
                    <p className="text-gray-500">Loading comments...</p>
                </div>
            ) : comments.length > 0 ? (
                <div className="space-y-3">
                    {comments.map((comment) => (
                        <div
                            key={comment.id}
                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Status Badge */}
                                    <div className="flex items-center gap-2 mb-2">
                                        {comment.is_approved ? (
                                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">
                                                ✅ Approved
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                                                🟡 Pending
                                            </span>
                                        )}
                                    </div>

                                    {/* Comment Content */}
                                    <p className="text-gray-800 mb-2">{comment.content}</p>

                                    {/* Author & Date */}
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span className="font-medium">{comment.author_name}</span>
                                        <span>•</span>
                                        <span>{formatDate(comment.created_at)}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 ml-4">
                                    {!comment.is_approved && (
                                        <button
                                            onClick={() => handleApprove(comment.id)}
                                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                            title="Approve"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                    )}
                                    {comment.is_approved && (
                                        <button
                                            onClick={() => handleReject(comment.id)}
                                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                            title="Reject"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(comment.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">
                        {filter === 'pending' ? 'No pending comments' :
                            filter === 'approved' ? 'No approved comments' :
                                'No comments yet on this post'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PostCommentSection;
